import { useEffect, useState } from "react";
import { Award, ShieldCheck, ExternalLink } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContentContext";
import { Reveal, SectionTag } from "./Reveal";
import { fetchCertifications, toAbsolute } from "@/lib/api";

const DEFAULTS = {
  ar: {
    tag: "الاعتمادات والشراكات",
    title: "معتمدون ومعترف بنا",
    subtitle: "نلتزم بأعلى معايير الصناعة العالمية عبر شراكات واعتمادات موثوقة.",
    empty: "قريبًا سنعرض شهاداتنا واعتماداتنا الرسمية.",
  },
  en: {
    tag: "Certifications & Partnerships",
    title: "Certified & Recognized",
    subtitle: "We uphold the highest global industry standards through trusted partnerships and certifications.",
    empty: "Our official certifications will be showcased soon.",
  },
  ku: {
    tag: "پێناسە و هاوبەشیەکان",
    title: "پەسندکراو و ناسراو",
    subtitle: "بەرزترین ستاندەردی جیهانی پەیڕەو دەکەین لە ڕێگای هاوبەشی و پێناسە متمانەپێکراوەکانەوە.",
    empty: "بەم زووانە ڕەسمی پێناسەکانمان پیشان دەدەین.",
  },
};

const PLACEHOLDERS = [
  { title: "ISO 27001", issuer: "Information Security", year: "2024" },
  { title: "Microsoft Partner", issuer: "Silver Competency", year: "2023" },
  { title: "AWS Partner", issuer: "Consulting Partner", year: "2024" },
  { title: "Best IT Award", issuer: "Iraq Tech Awards", year: "2025" },
];

export default function TrustBadges() {
  const { lang } = useLang();
  const { settings } = useSite();
  const [items, setItems] = useState(null);

  useEffect(() => {
    fetchCertifications().then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  const d = DEFAULTS[lang] || DEFAULTS.ar;
  const tag = settings[`trust_tag_${lang}`] || d.tag;
  const title = settings[`trust_title_${lang}`] || d.title;
  const subtitle = settings[`trust_subtitle_${lang}`] || d.subtitle;

  const list = items && items.length > 0 ? items : PLACEHOLDERS.map((p, i) => ({ id: `ph-${i}`, ...p, logo: "", website: "" }));
  const isPlaceholder = !items || items.length === 0;

  return (
    <section id="trust" className="relative section-pad overflow-hidden" data-testid="trust-badges-section">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 20% 30%, rgba(0,242,96,0.06), transparent 55%), radial-gradient(circle at 80% 70%, rgba(0,242,96,0.06), transparent 55%)",
        }}
      />
      <div className="relative z-10 mx-auto max-w-6xl px-5 sm:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <Reveal><SectionTag>{tag}</SectionTag></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mt-5">{title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-400 text-base sm:text-lg mt-4 max-w-2xl mx-auto">{subtitle}</p>
          </Reveal>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5" data-testid="trust-grid">
          {list.map((c, i) => {
            const inner = (
              <div
                className="group relative rounded-2xl p-5 sm:p-6 card-surface hover:border-[color:var(--primary)]/50 transition-all duration-500 hover:-translate-y-1 h-full flex flex-col items-center text-center"
                style={{ boxShadow: "0 8px 32px -12px rgba(0,242,96,0.12)" }}
                data-testid={`trust-badge-${i}`}
              >
                {c.logo ? (
                  <div className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl bg-white/95 grid place-items-center overflow-hidden mb-4 border border-white/10 group-hover:scale-105 transition-transform duration-500">
                    <img
                      src={toAbsolute(c.logo)}
                      alt={c.title}
                      loading="lazy"
                      className="max-h-full max-w-full object-contain p-2"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </div>
                ) : (
                  <div
                    className="h-20 w-20 sm:h-24 sm:w-24 rounded-2xl grid place-items-center mb-4 border border-[color:var(--primary)]/30 bg-[color:var(--primary)]/10 group-hover:scale-105 transition-transform duration-500"
                    style={{ boxShadow: "0 0 30px rgba(0,242,96,0.15)" }}
                  >
                    {i % 2 === 0 ? <ShieldCheck size={32} className="text-primary" /> : <Award size={32} className="text-primary" />}
                  </div>
                )}
                <div className="font-extrabold text-base sm:text-lg mb-1 line-clamp-2">{c.title}</div>
                {c.issuer && (
                  <div className="text-xs sm:text-sm text-slate-400 line-clamp-2">{c.issuer}</div>
                )}
                {c.year && (
                  <div
                    className="mt-3 inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-bold"
                    style={{ background: "rgba(0,242,96,0.12)", color: "var(--primary)" }}
                  >
                    {c.year}
                  </div>
                )}
                {c.website && (
                  <div className="absolute top-3 end-3 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ExternalLink size={14} className="text-slate-400" />
                  </div>
                )}
              </div>
            );
            return c.website ? (
              <a key={c.id || i} href={c.website} target="_blank" rel="noopener noreferrer" className="block h-full">{inner}</a>
            ) : (
              <div key={c.id || i} className="h-full">{inner}</div>
            );
          })}
        </div>

        {isPlaceholder && (
          <div className="mt-6 text-center text-xs text-slate-500" data-testid="trust-placeholder-hint">
            {d.empty}
          </div>
        )}
      </div>
    </section>
  );
}
